import type { ProfileConfigWidget } from 'src/api/redux/profileConfiguration/types';

/**
 * Build the POST body volume config for the system sites API from the widget's
 * code_sets and encounters. Each source becomes a keyed entry so the API
 * annotates per-site volume for that source.
 *
 * Result shape: { "<id>": { code_set: <id> } } for code sets,
 *               { "enc_<id>": { cohort_id: <id> } } for encounters.
 */
export const buildVolumeConfig = (
  widget: ProfileConfigWidget | null,
  minYear?: number,
  maxYear?: number
): Record<string, Record<string, any>> => {
  if (!widget) return {};

  const config: Record<string, Record<string, any>> = {};

  const dateRange =
    minYear != null || maxYear != null
      ? { gte: minYear, lte: maxYear }
      : undefined;

  const codeSets = [
    ...(widget.code_sets ?? []),
    ...(widget.code_set ? [widget.code_set] : []),
  ];

  for (const cs of codeSets) {
    config[String(cs.id)] = {
      code_set: cs.id,
      ...(dateRange && { date_range: dateRange }),
    };
  }

  const encounters = widget.encounters ?? [];
  for (const enc of encounters) {
    config[`enc_${enc.id}`] = {
      cohort_id: enc.id,
      ...(dateRange && { date_range: dateRange }),
    };
  }

  return config;
};

/**
 * Sum the volume for a row across all config keys.
 * The API returns e.g. `row.claims = { "10282": 500, "48536": 200 }`.
 * This sums all values for the given volume type.
 */
export const sumRowVolume = (
  row: Record<string, any>,
  volumeType: string
): number => {
  const volumeMap = row?.[volumeType];
  if (!volumeMap || typeof volumeMap !== 'object') return 0;

  return Object.values(volumeMap).reduce<number>(
    (sum, val) => sum + (Number(val) || 0),
    0
  );
};
